/* [2.14] "Tilføj og få fri fragt" – one-tap add-ons in the cart drawer.
 * Markup: snippets/np-cart-addons.liquid + snippets/np-cart-addon-card.liquid.
 * Re-renders the drawer with the theme's section-rendering method:
 * CartDrawer.getSectionsToRender() + CartDrawer.getSectionInnerHTML() (assets/cart-drawer.js).
 * Does NOT publish PUB_SUB_EVENTS.cartUpdate (CartItems.onCartUpdate would load main-cart-items into the drawer).
 */
(() => {
	if (customElements.get('np-cart-addons')) return;

	const escapeHtml = (value) =>
		String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

	const sizedImage = (url, width) => {
		if (!url) return '';
		const absolute = url.startsWith('//') ? `https:${url}` : url;
		return `${absolute}${absolute.includes('?') ? '&' : '?'}width=${width}`;
	};

	const money = (cents) =>
		typeof formatMoney === 'function' && window.theme && window.theme.moneyFormat
			? formatMoney(cents, window.theme.moneyFormat)
			: `${(cents / 100).toFixed(2).replace('.', ',')} kr`;

	// Rendered add-on cards per cart (product + exclusions), so a drawer re-render shows them at once
	// instead of hiding the row and popping it back in after a new fetch.
	const cache = new Map();

	class NpCartAddons extends HTMLElement {
		constructor() {
			super();
			this.onClick = this.onClick.bind(this);
			this.addEventListener('click', this.onClick);
		}

		cacheKey() {
			return `${this.dataset.productId}|${this.dataset.exclude || ''}`;
		}

		connectedCallback() {
			this.list = this.querySelector('[data-addons-list]');
			this.status = this.querySelector('[data-addons-status]');
			if (this.dataset.fallback === 'recommendations' && this.dataset.productId && cache.has(this.cacheKey())) {
				const html = cache.get(this.cacheKey());
				if (html && this.list) {
					this.list.innerHTML = html;
					this.hidden = false;
				}
				return;
			}
			if (this.dataset.fallback === 'recommendations' && this.dataset.productId && !this.loading) {
				this.loading = true;
				this.loadRecommendations();
			}
		}

		async loadRecommendations() {
			const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
			const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(this.dataset.productId)}&limit=10&intent=related`;
			try {
				const response = await fetch(url, { headers: { Accept: 'application/json' } });
				if (!response.ok) return;
				const { products = [] } = await response.json();
				const exclude = this.dataset.exclude || ',';
				const cap = Number(this.dataset.priceCap) || Number.MAX_SAFE_INTEGER;
				const limit = Number(this.dataset.limit) || 4;
				const eligible = products.filter((product) => {
					const variant = product.variants && product.variants.length === 1 ? product.variants[0] : null;
					return product.available && variant && variant.available && !exclude.includes(`,${product.id},`);
				});
				const preferred = eligible.filter((product) => product.variants[0].price <= cap);
				const others = eligible
					.filter((product) => product.variants[0].price > cap)
					.sort((a, b) => a.variants[0].price - b.variants[0].price);
				const picked = (preferred.length >= 3 ? preferred : preferred.concat(others)).slice(0, limit);
				const html = picked.map((product) => this.cardHTML(product)).join('');
				cache.set(this.cacheKey(), html);
				if (!picked.length || !this.list) return;
				this.list.innerHTML = html;
				this.hidden = false;
			} catch (error) {
				console.error('[np-cart-addons]', error);
			}
		}

		cardHTML(product) {
			const variant = product.variants[0];
			const title = escapeHtml(product.title);
			const href = escapeHtml(product.url);
			const aria = escapeHtml((this.dataset.labelAddAria || '').replace('[title]', product.title));
			const image = product.featured_image
				? `<img class="np-addon__img" src="${escapeHtml(sizedImage(product.featured_image, 240))}" width="120" height="120" loading="lazy" alt="">`
				: '';
			return `<li class="np-addon">
				<a class="np-addon__media" href="${href}" tabindex="-1" aria-hidden="true">${image}</a>
				<a class="np-addon__title" href="${href}">${title}</a>
				<span class="np-addon__price">${escapeHtml(money(variant.price))}</span>
				<button type="button" class="np-addon__add focus-inset" data-variant-id="${variant.id}" data-title="${title}" aria-label="${aria}">
					<span aria-hidden="true">+</span> ${escapeHtml(this.dataset.labelAdd || '')}
				</button>
			</li>`;
		}

		async onClick(event) {
			const button = event.target.closest('.np-addon__add');
			if (!button || !this.contains(button) || button.getAttribute('aria-busy') === 'true') return;
			event.preventDefault();

			const drawer = document.querySelector('cart-drawer');
			const sections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
			const title = button.dataset.title || '';
			button.setAttribute('aria-busy', 'true');
			button.disabled = true;
			if (this.status) this.status.textContent = '';

			try {
				const response = await fetch(`${window.routes.cart_add_url}.js`, {
					...fetchConfig('json'),
					body: JSON.stringify({
						items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
						sections,
						sections_url: window.location.pathname,
					}),
				});
				const state = await response.json();
				if (!response.ok || state.status) throw new Error(state.description || state.message || 'add failed');

				// fill factor 0–1 (the bar animates scaleX, see cart.js CartItems.progressNow)
				if (typeof CartItems !== 'undefined' && typeof CartItems.progressNow === 'function') {
					document.documentElement.style.setProperty('--progress-prev', String(CartItems.progressNow()));
				}

				if (drawer && state.sections) {
					drawer.getSectionsToRender().forEach((section) => {
						const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
						const html = state.sections[section.id];
						if (target && html) target.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
					});
					drawer.classList.remove('is-empty');
					// #CartDrawer was re-rendered, so its overlay lost the close listener added in the CartDrawer constructor.
					const overlay = document.getElementById('CartDrawer-Overlay');
					if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer, false));
					const container = document.getElementById('CartDrawer');
					trapFocus(container, document.getElementById('CartDrawer-Heading') || container);
					const message = (this.dataset.labelAdded || '').replace('[title]', title);
					// [UX-2026-10 P2-6] visible confirmation under the heading (role="status" also announces it)
					if (typeof drawer.showNotice === 'function') {
						drawer.showNotice(message, 'np-cart-added');
					} else {
						const live = document.getElementById('CartDrawer-LiveRegionText');
						if (live) setTimeout(() => { live.textContent = message; }, 150);
					}
				}

				fetch(`${window.routes.cart_url}.js`)
					.then((r) => r.json())
					.then((cart) => document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart, sectionId: 'np-cart-addons' } })))
					.catch(() => {});
			} catch (error) {
				button.removeAttribute('aria-busy');
				button.disabled = false;
				button.focus();
				if (this.status) this.status.textContent = this.dataset.labelError || '';
				console.error('[np-cart-addons]', error);
			}
		}
	}

	customElements.define('np-cart-addons', NpCartAddons);
})();
